import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ReviewsOutlinedIcon from '@mui/icons-material/ReviewsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import CreditCardIcon from '@mui/icons-material/CreditCard';

export const ADMIN_TABS = [
  { key: 'overview', label: 'Dashboard Overview', icon: SpaceDashboardOutlinedIcon },
  { key: 'users', label: 'Users Management', icon: PeopleAltOutlinedIcon },
  { key: 'roles', label: 'Roles & Permissions', icon: ShieldOutlinedIcon },
  { key: 'auctions', label: 'Normal Auctions', icon: GavelOutlinedIcon },
  { key: 'secondHand', label: 'Second-Hand Auctions', icon: StorefrontOutlinedIcon },
  { key: 'products', label: 'Products Management', icon: Inventory2OutlinedIcon },
  { key: 'brands', label: 'Brand Management', icon: BrandingWatermarkIcon },
  { key: 'bids', label: 'Bids', icon: EmojiEventsOutlinedIcon },
  { key: 'reviews', label: 'Reviews', icon: ReviewsOutlinedIcon },
  { key: 'orders', label: 'Orders', icon: ReceiptLongOutlinedIcon },
  { key: 'transactions', label: 'Transactions', icon: ReceiptLongOutlinedIcon },
  { key: 'packsCoupons', label: 'Packs & Coupons', icon: RedeemOutlinedIcon },
  { key: 'serverHealth', label: 'Server Health', icon: HealthAndSafetyOutlinedIcon },
  { key: 'analytics', label: 'Analytics', icon: InsightsOutlinedIcon },
  { key: 'googleAnalytics', label: 'Google Analytics', icon: QueryStatsOutlinedIcon },
  { key: 'paymentSettings', label: 'Payment Settings', icon: CreditCardIcon },
];

const AdminSidebar = ({ activeTab, onTabChange }) => {
  const theme = useTheme();

  const handleSelect = (tabKey) => {
    if (onTabChange) {
      onTabChange(tabKey);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'transparent',
        color: 'text.primary',
      }}
    >
      <Box sx={{ px: 3, pt: 3.5, pb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt=""
            sx={{ width: 38, height: 38, objectFit: 'contain' }}
          />
          <Box>
            <Typography sx={{ fontSize: '.76rem', fontWeight: 800, letterSpacing: '.18em', lineHeight: 1.2 }}>
              VITRIA
            </Typography>
            <Typography sx={{ color: 'primary.light', fontSize: '.62rem', fontWeight: 800, letterSpacing: '.12em' }}>
              ADMINISTRATION
            </Typography>
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={750} gutterBottom>
          Control center
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Operations, trust and marketplace health.
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 2 }}>
        <List disablePadding>
          {ADMIN_TABS.map(({ key, label, icon: Icon }) => {
            const selected = activeTab === key;
            return (
              <ListItemButton
                key={key}
                selected={selected}
                onClick={() => handleSelect(key)}
                sx={{
                  position: 'relative',
                  mb: 0.5,
                  borderRadius: 3,
                  px: 2,
                  py: 1.25,
                  color: selected ? 'text.primary' : 'text.secondary',
                  '& .MuiListItemIcon-root': {
                    color: selected ? theme.palette.primary.light : 'text.secondary'
                  },
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                    '&::before': {
                      position: 'absolute',
                      left: 0,
                      width: 3,
                      height: 22,
                      borderRadius: 99,
                      bgcolor: 'primary.main',
                      content: '""',
                    },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.19)
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ variant: 'body1', fontWeight: selected ? 600 : 500 }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

AdminSidebar.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func,
};

export default AdminSidebar;
